import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ColorAreaDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    withAlpha
} from "../../utils/color";
import {
    webStyle
} from "../../utils";

export const COLOR_AREA_THUMB_SHADOW_ALPHA = 0.3;

const stylesheet = StyleSheet.create({
    disabled: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    chip: {
        justifyContent: "center",
        pointerEvents: "none",
        position: "absolute"
    },
    container: {
        ...webStyle({
            cursor: "crosshair",
            touchAction: "none",
            userSelect: "none"
        })
    },
    dragging: {
        ...webStyle({
            cursor: "grabbing"
        })
    },
    layer: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    frame: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    thumb: {
        pointerEvents: "none",
        position: "absolute"
    },
    row: {
        pointerEvents: "none",
        position: "absolute"
    },
    clip: {
        overflow: "hidden"
    }
});

export const useStyles = ({
    isFocusVisible,
    shapeConfigs,
    isDisabled,
    isDragging,
    thumbColor,
    iconSizes,
    ringColor,
    radiuses,
    borders,
    isFlush,
    colors,
    height,
    radius,
    thumb
}: ColorAreaDynamicStyleType) => {
    const outerRadius = radius ?? radiuses.surface;

    const corner = getCornerStyle({
        radius: outerRadius,
        shapeConfigs
    });

    const flushCorner = isFlush ? {
        borderBottomStartRadius: 0,
        borderBottomEndRadius: 0
    } : {};

    const thumbShadowColor = withAlpha({
        alpha: COLOR_AREA_THUMB_SHADOW_ALPHA,
        color: colors.system.shadow
    });

    const styles = {
        overlay: {
            backgroundColor: colors.system.state.overlay.disabled.neutral
        } as Mutable<ViewStyle>,
        frame: {
            borderColor: withAlpha({
                color: colors.content.border.default,
                alpha: 0.5
            }),
            borderWidth: isFlush ? 0 : borders.line,
            ...corner,
            ...flushCorner
        } as Mutable<ViewStyle>,
        thumb: {
            boxShadow: [
                {
                    spreadDistance: borders.line,
                    color: thumbShadowColor,
                    offsetX: 0,
                    offsetY: 0
                },
                {
                    spreadDistance: borders.line,
                    color: thumbShadowColor,
                    offsetX: 0,
                    offsetY: 0,
                    inset: true
                }
            ],
            ...getCornerStyle({
                radius: radiuses.full,
                shapeConfigs
            }),
            start: thumb.x - iconSizes.medium / 2,
            top: thumb.y - iconSizes.medium / 2,
            opacity: isDisabled ? 0.5 : 1,
            backgroundColor: thumbColor,
            borderWidth: borders.focus,
            zIndex: isDragging ? 2 : 1,
            height: iconSizes.medium,
            width: iconSizes.medium,
            borderColor: ringColor
        } as Mutable<ViewStyle>,
        container: {
            height,
            ...corner,
            ...flushCorner,
            ...getFocusRingStyle({
                isInset: isFlush,
                isFocusVisible,
                borders,
                colors
            })
        } as Mutable<ViewStyle>,
        clip: {
            ...corner,
            ...flushCorner
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
